import { toTask, when } from '@execonline-inc/maybe-adapter';
import { noop } from '@kofno/piper';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { fromNullableT } from '../../../../TaskExt';
import { useAutoScroll } from '../AutoScrollContext';
import { isScrolledToBottom } from '../utils';

const managedState = (
  element: HTMLDivElement,
  setIsAutoScrollEnabled: (arg: boolean) => void,
  lastScrollPositionRef: MutableRefObject<number>,
): void => {
  toTask('not-bottom-scrolled', when(isScrolledToBottom(element), true))
    .orElse(() =>
      toTask('some-error', when(element.scrollTop < lastScrollPositionRef.current, false)),
    )
    .fork(noop, setIsAutoScrollEnabled);
};

const manageScrollState = (
  element: HTMLDivElement,
  setIsAutoScrollEnabled: (arg: boolean) => void,
  lastScrollPositionRef: MutableRefObject<number>,
) => {
  managedState(element, setIsAutoScrollEnabled, lastScrollPositionRef);
  return (lastScrollPositionRef.current = element.scrollTop);
};

const useScrollHandling = (ref: React.RefObject<HTMLDivElement | null>) => {
  const lastScrollPositionRef = useRef(0);
  const { setIsAutoScrollEnabled } = useAutoScroll();

  const handleScroll = useCallback(() => {
    fromNullableT(ref.current).fork(noop, (el) =>
      manageScrollState(el, setIsAutoScrollEnabled, lastScrollPositionRef),
    );
  }, [ref, setIsAutoScrollEnabled]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [handleScroll, ref]);

  return { handleScroll };
};

export default useScrollHandling;
